import { useState } from 'react';

export interface Testimonial {
  name: string;
  location: string;
  rating: number;
  text: string;
  service: string;
  avatar?: string;
}

export interface TestimonialsSectionProps {
  testimonials: Testimonial[];
}

export default function TestimonialsSection({ testimonials }: TestimonialsSectionProps) {
  const [active, setActive] = useState(0);
  const len = testimonials.length;

  const prev = () => setActive((a) => (a - 1 + len) % len);
  const next = () => setActive((a) => (a + 1) % len);

  const getIndex = (offset: number) => (active + offset + len) % len;

  return (
    <section className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-bronze text-xs font-medium uppercase tracking-widest mb-3">Client Reviews</p>
          <h2 className="font-serif text-4xl md:text-5xl font-semibold text-charcoal mb-4">
            What Our Clients Say
          </h2>
          <div className="flex items-center justify-center gap-1 mb-2">
            {[1,2,3,4,5].map(s => (
              <div key={s} className="w-5 h-5 flex items-center justify-center">
                <i className="ri-star-fill text-bronze text-lg"></i>
              </div>
            ))}
          </div>
          <p className="text-gray-400 text-sm">500+ satisfied clients across Brooklyn & NYC</p>
        </div>

        {/* Desktop: 3 visible */}
        <div className="hidden md:grid grid-cols-3 gap-6 mb-10">
          {[-1, 0, 1].map((offset) => {
            const t = testimonials[getIndex(offset)];
            const isCenter = offset === 0;
            return (
              <div
                key={getIndex(offset)}
                onClick={() => setActive(getIndex(offset))}
                className={`relative bg-ivory rounded-2xl p-8 transition-all duration-300 cursor-pointer ${
                  isCenter ? 'scale-105 z-10' : 'opacity-70 hover:opacity-90'
                }`}
              >
                {/* Large quote mark */}
                <span className="absolute top-4 right-6 font-serif text-8xl text-bronze/10 leading-none select-none">&ldquo;</span>
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <div key={i} className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-bronze text-sm"></i>
                    </div>
                  ))}
                </div>
                <p className="text-gray-700 text-base italic leading-relaxed mb-6 font-serif">&ldquo;{t.text}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center shrink-0">
                    <i className="ri-user-3-line text-sage text-lg"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-charcoal text-sm">{t.name}</p>
                    <p className="text-gray-400 text-xs">{t.location} · {t.service}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: single */}
        <div className="md:hidden mb-6">
          <div className="relative bg-ivory rounded-2xl p-8">
            <span className="absolute top-4 right-6 font-serif text-8xl text-bronze/10 leading-none select-none">&ldquo;</span>
            <div className="flex gap-0.5 mb-4">
              {Array.from({ length: testimonials[active].rating }).map((_, i) => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-bronze text-sm"></i>
                </div>
              ))}
            </div>
            <p className="text-gray-700 text-base italic leading-relaxed mb-6 font-serif">&ldquo;{testimonials[active].text}&rdquo;</p>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-sage/20 flex items-center justify-center shrink-0">
                <i className="ri-user-3-line text-sage text-lg"></i>
              </div>
              <div>
                <p className="font-semibold text-charcoal text-sm">{testimonials[active].name}</p>
                <p className="text-gray-400 text-xs">{testimonials[active].location} · {testimonials[active].service}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between md:justify-end gap-3">
          <div className="flex gap-1.5">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`h-1.5 rounded-full transition-all cursor-pointer ${
                  i === active ? 'w-6 bg-bronze' : 'w-1.5 bg-warmgray'
                }`}
              ></button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={prev}
              className="w-10 h-10 flex items-center justify-center border border-bronze/30 rounded-full hover:bg-bronze hover:text-white hover:border-bronze transition-colors cursor-pointer text-bronze"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={next}
              className="w-10 h-10 flex items-center justify-center border border-bronze/30 rounded-full hover:bg-bronze hover:text-white hover:border-bronze transition-colors cursor-pointer text-bronze"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
